import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { type FieldOnly, PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  CreateGoalSettingBody,
  CreateGroupLogBody,
  CreateSessionGroupBody,
  EditGoalSettingBody,
  EditGoalSettingStatusBody,
  EditGroupLogBody,
  EditSwimmingGoalSettingStatusBody,
  Goal,
  GoalSetting,
  GoalSettingEditLog,
  GoalSettingOption,
  GoalSessionGroupCreated,
  GoalSettingSession,
  GoalSettingWithSession,
  PersonalGoalSettingSession,
  SendSessionPlanBody,
  SwimmingGoalSetting,
  SwimmingGoalSettingEditLogResponse,
  SwimmingMemberType,
} from './model'

export enum GetGoalSettingsStatusFilter {
  ALL = 'ALL',
  NOT_STARTED = 'NOT_STARTED',
  IN_TRANING = 'IN_TRAINING',
  NEEDS_IMPROVEMENT = 'NEEDS_IMPROVEMENT',
  SUCCESS = 'SUCCESS',
}

export enum GetSwimmingGoalSettingsStatusFilter {
  ALL = 'ALL',
  NOT_STARTED = 'NOT_STARTED',
  IN_TRAINING = 'IN_TRAINING',
  NEEDS_IMPROVEMENT = 'NEEDS_IMPROVEMENT',
  SUCCESS = 'SUCCESS',
}

type GetGoalSettingsOpt = PaginationOpt & {
  yearMonth?: string
  memberId?: number
  status?: GetGoalSettingsStatusFilter
  goalType?: string
  trainingType?: string
}

type GetSwimmingGoalSettingsOpt = PaginationOpt & {
  status: GetSwimmingGoalSettingsStatusFilter
  goalId: number
  memberType: 'ALL' | 'ADULT' | 'KID'
}

type GetSwimmingGoalSettingsByTrainerOpt = PaginationOpt & {
  trainerId: number
  status: GetSwimmingGoalSettingsStatusFilter
}

export const GoalSettingService = (client: AxiosInstance) => {
  return {
    async getGoalSettings(opt: GetGoalSettingsOpt): Promise<PaginationResponse<GoalSetting>> {
      const params = {
        page: opt.page,
        pageSize: opt.pageSize,
        yearMonth: opt.yearMonth,
        query: opt.query,
        memberId: opt.memberId,
        status: opt.status ?? GetGoalSettingsStatusFilter.ALL,
        goalType: opt.goalType,
        trainingType: opt.trainingType,
      }
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/goal-setting',
          { params }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, GoalSetting)),
      }
    },
    async getSwimmingGoalSettings(
      opt: GetSwimmingGoalSettingsOpt
    ): Promise<PaginationResponse<SwimmingGoalSetting>> {
      const params = {
        page: opt.page,
        pageSize: opt.pageSize,
        query: opt.query,
        goalId: opt.goalId,
        memberType: opt.memberType,
        status: opt.status ?? GetSwimmingGoalSettingsStatusFilter.ALL,
      }
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/goal-setting/swimming',
          { params }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingGoalSetting)
        ),
      }
    },
    async getSwimmingGoalSettingsByTrainer(
      opt: GetSwimmingGoalSettingsByTrainerOpt
    ): Promise<PaginationResponse<SwimmingGoalSetting>> {
      const params = {
        trainerId: opt.trainerId,
        page: opt.page,
        pageSize: opt.pageSize,
        query: opt.query,
        status: opt.status ?? GetSwimmingGoalSettingsStatusFilter.ALL,
      }
      const { data } = await wrapApiCall(() =>
        client.get<{ pagination: Record<string, unknown>; data: Record<string, unknown>[] }>(
          '/goal-setting/swimming/by-trainer',
          { params }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) =>
          deserialize(item, SwimmingGoalSetting)
        ),
      }
    },
    async createSwimmingGoalSetting(
      memberId: number,
      memberType: string,
      goalId: number,
      otherPersonalGoal: string,
      startDate: string,
      endDate: string,
      detail: string
    ): Promise<SwimmingGoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.post<SwimmingGoalSetting>(`/goal-setting/swimming`, {
          memberId,
          memberType,
          goalId,
          otherPersonalGoal,
          startDate,
          endDate,
          detail,
        })
      )
      return data
    },
    async updateSwimmingGoalSetting(
      memberType: string,
      swimmingGoalSettingId: number,
      goalId: number,
      otherPersonalGoal: string,
      startDate: string,
      endDate: string,
      detail: string,
      reason: string
    ): Promise<Goal> {
      const { data } = await wrapApiCall(() =>
        client.patch<Goal>(`/goal-setting/swimming/${swimmingGoalSettingId}`, {
          memberType,
          goalId,
          otherPersonalGoal,
          startDate,
          endDate,
          detail,
          reason,
        })
      )
      return data
    },
    async getGoals(): Promise<Goal[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/goal-setting/goals`)
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, Goal)) || []
    },
    async getSwimmingGoals(memberType: 'ALL' | 'ADULT' | 'KID'): Promise<Goal[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/goal-setting/swimming/goals`, {
          params: { memberType },
        })
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, Goal)) || []
    },
    async getSwimmingGoalsById(userId?: number): Promise<SwimmingGoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-setting/swimming/${userId}`)
      )
      return deserialize(data, SwimmingGoalSetting)
    },
    async getSwimmingMemberType(): Promise<SwimmingMemberType[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/goal-setting/member-types`)
      )
      return (
        data?.map((item: Record<string, unknown>) => deserialize(item, SwimmingMemberType)) || []
      )
    },
    async getGoalSettingSessions(id: string | number): Promise<GoalSettingWithSession> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-setting/${id}`)
      )

      return deserialize(data, GoalSettingWithSession)
    },
    async getSwimmingGoalSettingSessions(
      id: string | number,
      params?: PaginationOpt
    ): Promise<PaginationResponse<GoalSettingSession>> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          pagination?: Record<string, unknown>
          data?: Record<string, unknown>[]
          total?: number
        }>(`/goal-setting/swimming/${id}/sessions`, { params })
      )

      const list = Array.isArray(data?.data) ? data.data : []

      const pagination = data?.pagination
        ? deserialize(data.pagination, PageInfo)
        : PageInfo.from({
            page: params?.page ?? 1,
            pageSize: params?.pageSize ?? list.length,
            total: data?.total ?? list.length,
          })

      return {
        pagination,
        data: list.map((item: Record<string, unknown>) => deserialize(item, GoalSettingSession)),
      }
    },
    async getGoalSettingById(id: string | number): Promise<GoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-setting/${id}`)
      )
      return deserialize(data, GoalSetting)
    },
    async getGoalSettingSessionDetail(id: string | number): Promise<GoalSettingSession> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-setting/sessions/${id}`)
      )
      return deserialize(data, GoalSettingSession)
    },
    async getPersonalGoalSettingSessionDetail(
      id: string | number
    ): Promise<PersonalGoalSettingSession> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-sessions/groups/${id}`)
      )
      return deserialize(data, PersonalGoalSettingSession)
    },
    async getSwimmingGoalSettingSessionDetail(id: string | number): Promise<GoalSettingSession> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/goal-setting/swimming/sessions/${id}`)
      )
      return deserialize(data, GoalSettingSession)
    },
    async getGoalSettingEditLogs(id: string | number): Promise<GoalSettingEditLog[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/goal-setting/${id}/edit-logs`)
      )
      if (!Array.isArray(data)) {
        return []
      }
      return data.map((item: Record<string, unknown>) => deserialize(item, GoalSettingEditLog))
    },
    async getSwimmingGoalSettingEditLogs(
      id: string | number
    ): Promise<SwimmingGoalSettingEditLogResponse[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/goal-setting/swimming/${id}/edit-logs`)
      )
      if (!Array.isArray(data)) {
        return []
      }
      return data.map((item: Record<string, unknown>) =>
        deserialize(item, SwimmingGoalSettingEditLogResponse)
      )
    },
    async createGoalSetting(body: CreateGoalSettingBody): Promise<GoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/goal-setting`, body)
      )
      return deserialize(data, GoalSetting)
    },
    async updateGoalSetting(id: string, body: EditGoalSettingBody): Promise<GoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/goal-setting/${id}`, body)
      )
      return deserialize(data, GoalSetting)
    },
    async updateGoalSettingStatus(
      id: string,
      body: EditGoalSettingStatusBody
    ): Promise<GoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/goal-setting/${id}/complete`, body)
      )
      return deserialize(data, GoalSetting)
    },
    async getMuscles(): Promise<GoalSettingOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<GoalSettingOption[]>(`/goal-setting/muscle`)
      )
      return Array.isArray(data) ? data : []
    },
    async getExerciseCategories(): Promise<GoalSettingOption[]> {
      const { data } = await wrapApiCall(() =>
        client.get<GoalSettingOption[]>(`/goal-setting/exercise-category`)
      )
      return Array.isArray(data) ? data : []
    },
    async createSessionGroup(body: CreateSessionGroupBody): Promise<GoalSessionGroupCreated> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/goal-sessions`, body)
      )
      return deserialize(data, GoalSessionGroupCreated)
    },
    async createSessionLog(groupId: string | number, body: CreateGroupLogBody): Promise<void> {
      await wrapApiCall(() => client.post(`/goal-sessions/${groupId}/logs`, body))
    },
    async editSessionLog(logId: string | number, body: EditGroupLogBody): Promise<void> {
      await wrapApiCall(() => client.patch(`/goal-sessions/${logId}/logs`, body))
    },
    async sendSessionPlan(body: SendSessionPlanBody): Promise<void> {
      await wrapApiCall(() => client.post(`/goal-sessions/send-plan`, body))
    },
    async updateSwimmingGoalSettingStatus(
      id: string,
      body: EditSwimmingGoalSettingStatusBody
    ): Promise<GoalSetting> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown> | string | null>(
          `/goal-setting/swimming/${id}/complete`,
          body
        )
      )
      if (data == null || (typeof data === 'string' && data.trim() === '')) {
        return GoalSetting.from({ id: Number(id) } as unknown as FieldOnly<GoalSetting>)
      }
      return deserialize(data, GoalSetting)
    },
  }
}
